<template>
  <div>
    <div class="branch-select__head">
      <div class="branch-select__title">{{ title }}</div>
      <div class="branch-select__hint">
        Меню и доступные рестораны могут отличаться в зависимости от локации
      </div>
    </div>

    <UiKitRadioGroup
      :model-value="selectedBranchId ?? undefined"
      color="danger"
      class="branch-list"
      @update:model-value="onPick"
    >
      <UiKitRadio
        v-for="(branch, idx) in branches"
        :key="branch.id || idx"
        :value="branch.id"
        class="branch-item"
        :class="{ 'branch-item--active': selectedBranchId === branch.id }"
      >
        <span class="branch-item__row">
          <span class="branch-item__icon">
            <UIcon name="i-heroicons-building-storefront" class="w-5 h-5" />
          </span>
          <span class="branch-item__addr">
            <span class="branch-item__street">{{ branch.address.street }}</span>
            <span class="branch-item__city">{{ branch.address.city }}</span>
          </span>
        </span>
      </UiKitRadio>
    </UiKitRadioGroup>

    <UiButtonMain
      :disabled="!selectedBranchId"
      :label="submitLabel"
      class="mt-4"
      color="red"
      size="lg"
      block
      @click="$emit('submit')"
    />
  </div>
</template>

<script setup lang="ts">
interface Branch {
  id: string | number
  name: string
  address: {
    city: string
    street: string
  }
  working_hours?: { hours: string }
  phone?: string
  is_active?: boolean
}

interface Props {
  branches: Branch[]
  title: string
  submitLabel: string
  selectedBranchId?: string | number | null
  radioPrefix?: string
}

const props = withDefaults(defineProps<Props>(), {
  selectedBranchId: null,
  radioPrefix: 'branch',
})

const emit = defineEmits<{
  select: [branch: Branch]
  submit: []
}>()

function onPick(value: string | number | undefined): void {
  const branch = props.branches.find(b => b.id === value)
  if (branch) emit('select', branch)
}
</script>

<style scoped lang="scss">
.branch-select {
  &__head {
    @apply mb-4;
  }

  &__title {
    @apply text-lg font-bold text-hui-foreground;
  }

  &__hint {
    @apply text-sm text-hui-foreground/50 mt-1;
  }
}

// Список филиалов (UiKitRadioGroup): карточки-радио
.branch-list {
  @apply max-h-96 overflow-y-auto;

  :deep(.branch-item) {
    @apply w-full items-center p-3 rounded-hui-md border transition-colors;
    @apply bg-hui-content1 border-hui-divider;

    &:hover {
      @apply bg-hui-content2;
    }

    // текстовая обёртка UiKitRadio занимает всю ширину строки
    > span:last-child {
      @apply flex-1 min-w-0;
    }
  }

  :deep(.branch-item--active) {
    @apply border-hui-danger bg-hui-danger/5;
  }
}

.branch-item__row {
  @apply flex items-center gap-3 w-full;
}

.branch-item__icon {
  @apply flex items-center justify-center w-9 h-9 rounded-full shrink-0;
  @apply bg-hui-default-100 text-hui-foreground/70;
}

.branch-item__addr {
  @apply flex flex-col min-w-0;
}

.branch-item__street {
  @apply text-base font-medium text-hui-foreground truncate;
}

.branch-item__city {
  @apply text-sm text-hui-foreground/50 truncate;
}
</style>
